/**
 * Created by neo on 23.12.22.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { ArticleEntry } from '../../../Model/Explore/ArticleEntry';
import { ExploreContentViewVideoPlayer } from './ExploreContentViewVideoPlayer';
import { ExploreContentViewArticleContent } from './ExploreContentViewArticleContent';
import { ShareButton } from '../../ShareButton';
import { useContentMetaTags } from '../../../Hooks/useContentMetaTags';
import { useLocalized } from '../../../Hooks/useLocalized';
import { useLocalizedImage } from '../../../Hooks/useLocalizedMedia';
import { getMediaLarge } from '../../../Model/Media/Media';
import { useRootStore } from '../../../Store/useRootStore';
import { usePlausible } from '../../../Contexts/PlausibleContext';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ScrollContainer = styled.div`
  overflow-y: auto;
  height: 100vh;
  background: #fafafa;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const HeroSection = styled.div`
  width: 100%;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const ContentCard = styled.article`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  background: white;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 20px;
  }
`;

export type ExploreContentViewArticleProps = {
  entry: ArticleEntry;
};

const ShareRow = styled.div`
  max-width: 400px;
  margin: 0 auto;
  width: 100%;
`;

export const ExploreContentViewArticle: React.FC<ExploreContentViewArticleProps> = observer(({ entry }) => {
  const { engagement } = useRootStore();
  const { trackPlausible } = usePlausible();
  const title = useLocalized(entry.name);
  const description = useLocalized(entry.description);
  const entryImage = useLocalizedImage(entry.images ?? []);
  const imageUrl = entryImage ? getMediaLarge(entryImage) : undefined;

  useContentMetaTags({ title, description, imageUrl });

  const hasTriggeredRef = React.useRef(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const openedTimestampRef = React.useRef<number>(Date.now());

  // Reset tracking when entry changes
  React.useEffect(() => {
    openedTimestampRef.current = Date.now();
    hasTriggeredRef.current = false;
  }, [entry.id]);

  React.useEffect(() => {
    trackPlausible('Page View', { pageTitle: title, contentType: entry.type, contentId: entry.id });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id, title, entry.type]);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Wait for layout to update
    requestAnimationFrame(() => {
      const { scrollHeight, clientHeight } = container;
      const isCloseToEnd = scrollHeight <= clientHeight + 96; // Same threshold as scroll handler

      if (isCloseToEnd && !hasTriggeredRef.current) {
        hasTriggeredRef.current = true;
        const durationSeconds = Math.round((Date.now() - openedTimestampRef.current) / 1000);
        engagement.sendArticleRead(entry, durationSeconds).then((res) => engagement.showPointsModalPromise(res));
        trackPlausible('Content Complete', { seconds: `${durationSeconds}s`, pageTitle: title, contentType: entry.type, contentId: entry.id });
      }
    });
  }, [entry, engagement]);

  const handleScrollEnd = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      try {
        const target = event?.currentTarget;
        if (!target || hasTriggeredRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = target;
        const isCloseToEnd = scrollTop + clientHeight >= scrollHeight - 96;

        if (isCloseToEnd) {
          hasTriggeredRef.current = true;
          const durationSeconds = Math.round((Date.now() - openedTimestampRef.current) / 1000);
          engagement.sendArticleRead(entry, durationSeconds).then((res) => engagement.showPointsModalPromise(res));
          trackPlausible('Content Complete', { seconds: `${durationSeconds}s`, pageTitle: title, contentType: entry.type, contentId: entry.id });
        }
      } catch (error) {
        console.error('Scroll handler error:', error);
      }
    },
    [engagement, entry, trackPlausible],
  );

  return (
    <ScrollContainer ref={scrollContainerRef} onScroll={handleScrollEnd}>
      <PageContainer>
        <HeroSection>
          <ExploreContentViewVideoPlayer entry={entry} />
        </HeroSection>
        <ShareRow>
          <ShareButton data={{ title, description, imageUrl, contentType: 'article', contentId: entry.id }} />
        </ShareRow>
        <ContentCard>
          <ExploreContentViewArticleContent entry={entry} />
        </ContentCard>
      </PageContainer>
    </ScrollContainer>
  );
});
